import { TZDate } from '@date-fns/tz';
import { endOfDay } from 'date-fns';
import { toCents, toDollars } from '@/utils/utilities.mjs';

// Campaign dates are date-only, and a date-only string parses as midnight UTC,
// which is the previous evening in Eastern time: bounds built that way expired
// campaigns a day early. They resolve in the store's own timezone instead, the
// same zone ContactCX.js uses for business hours.
const CAMPAIGN_TIME_ZONE = 'America/New_York';

/**
 * Shared cart/variant pricing primitives used by both discount strategies.
 * These functions are strategy-agnostic and contain no CMS settings logic.
 * Import and re-export them from each strategy file to keep the public API stable.
 */

// Parses the raw Shopify float amount. Cents-level rounding is omitted as 
// no intermediate math is performed on the raw variant values.
export const variantFullPrice = (variant) => parseFloat(variant?.price?.amount || 0);

export const applyPercentageDiscount = (fullPrice, number) => {
  const percentage = parseFloat(number) / 100;
  const fullCents = toCents(fullPrice);
  const saleCents = Math.round(fullCents - fullCents * percentage);
  return toDollars(saleCents);
};

export const applyFixedAmountDiscount = (fullPrice, amount) => {
  const discountCents = toCents(parseFloat(amount) || 0);
  const fullCents = toCents(fullPrice);
  const saleCents = Math.max(0, fullCents - discountCents);
  return toDollars(saleCents);
};

export const cartLineFullPrice = (cartLine) => {
  const unitCents = toCents(cartLine.merchandise?.price?.amount);
  return toDollars(unitCents * (cartLine.quantity ?? 1));
};

// A CMS date names a whole day, but `now` is an instant: this resolves it to
// the day's first (start bound) or last (end bound) instant, in store time.
// Built from numbers rather than a parsed string: TZDate's string path runs
// through the native Date parser first and applies the offset twice.
const dayBoundary = (dateString, atEndOfDay) => {
  const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);
  const start = new TZDate(year, month - 1, day, CAMPAIGN_TIME_ZONE);
  return atEndOfDay ? endOfDay(start) : start;
};

// Campaign dates are optional CMS fields (campaign_settings.prices_valid_from/to),
// inclusive on both ends. A missing bound leaves that side open-ended.
export const isCampaignActive = (discountSettings, now = new Date()) => {
  const { prices_valid_from, prices_valid_to } = discountSettings ?? {};

  if (prices_valid_from && now < dayBoundary(prices_valid_from, false)) return false;
  if (prices_valid_to && now > dayBoundary(prices_valid_to, true)) return false;

  return true;
};

// Pre-discount cart total (sum of full line prices)
// discounts are shown separately via cartSavings; the discounted amount is
// cartTotal ("Total Before Tax" in CartOrderTotals). subtotal − savings = total.
export const cartSubtotal = (cart, cartLineFullPriceFn) => {
  if (!cart) return 0;

  const subtotalCents = cart.lines.reduce(
    (carry, item) => carry + toCents(cartLineFullPriceFn(item)),
    0,
  );

  return toDollars(subtotalCents);
};

export const cartTotal = (cart, discountSettings, cartLineSalePriceFn) => {
  if (!cart) return 0;

  const totalCents = cart.lines.reduce(
    (carry, item) => carry + toCents(cartLineSalePriceFn(item, discountSettings)),
    0,
  );

  return toDollars(totalCents);
};

export const cartSavings = (
  cart,
  discountSettings,
  cartLineFullPriceFn,
  cartLineSalePriceFn,
) => {
  if (!cart) return 0;

  const savingsCents = cart.lines.reduce((carry, item) => {
    const fullCents = toCents(cartLineFullPriceFn(item));
    const saleCents = toCents(cartLineSalePriceFn(item, discountSettings));
    return carry + (fullCents - saleCents);
  }, 0);

  return toDollars(savingsCents);
};
